/**
 * Confidentiality passes down from a record to the records under it: a record is confidential when it is marked
 * itself or when any of its parents is, each parent counting by its own effective value, so the strictest parent
 * wins. The parents are an agreement's supplier, a request's supplier and agreements, and a document's supplier,
 * agreements and owning request. A library document a request only links does not inherit from that request, and the
 * supplier and agreement hierarchies pass no confidentiality down.
 *
 * It is derived on every sync from the parents' current marks rather than copied at creation, so unmarking a
 * parent or moving a child to an open parent opens the child again, unless the child carries its own mark.
 */
export const CONFIDENTIALITY_PARENT_TYPES = ['supplier', 'agreement', 'request'] as const

export type ConfidentialityParentType = (typeof CONFIDENTIALITY_PARENT_TYPES)[number]

/** A confidential parent a record inherits its confidentiality from. */
export type ConfidentialityParent = { type: ConfidentialityParentType; id: string }

/** A parent with its effective value: its own mark or one it inherits. */
type ParentConfidentiality = { id: string; isConfidential: boolean }

const confidentialParents = (
  type: ConfidentialityParentType,
  parents: readonly (ParentConfidentiality | null)[]
): ConfidentialityParent[] => parents.flatMap((parent) => (parent?.isConfidential === true ? [{ type, id: parent.id }] : []))

export const isConfidentialWithParents = (isMarked: boolean, inheritsFrom: readonly ConfidentialityParent[]): boolean =>
  isMarked || inheritsFrom.length > 0

export const agreementInheritsFrom = (agreement: {
  supplierId: string
  isSupplierConfidential: boolean
}): ConfidentialityParent[] =>
  confidentialParents('supplier', [{ id: agreement.supplierId, isConfidential: agreement.isSupplierConfidential }])

export const requestInheritsFrom = (request: {
  supplierId: string | null
  isSupplierConfidential: boolean
  confidentialAgreementIds: readonly string[]
}): ConfidentialityParent[] => [
  ...confidentialParents('supplier', [
    request.supplierId == null ? null : { id: request.supplierId, isConfidential: request.isSupplierConfidential },
  ]),
  ...request.confidentialAgreementIds.map((id): ConfidentialityParent => ({ type: 'agreement', id })),
]

/** A document's parents, not the requests that only link it. */
export const documentInheritsFrom = (document: {
  supplier: ParentConfidentiality | null
  agreements: readonly ParentConfidentiality[]
  request: ParentConfidentiality | null
}): ConfidentialityParent[] => [
  ...confidentialParents('supplier', [document.supplier]),
  ...confidentialParents('agreement', document.agreements),
  ...confidentialParents('request', [document.request]),
]

export const isAgreementConfidential = (agreement: {
  isConfidential: boolean
  supplierId: string
  isSupplierConfidential: boolean
}) => isConfidentialWithParents(agreement.isConfidential, agreementInheritsFrom(agreement))

export const isRequestConfidential = (request: {
  isConfidential: boolean
  supplierId: string | null
  isSupplierConfidential: boolean
  confidentialAgreementIds: readonly string[]
}) => isConfidentialWithParents(request.isConfidential, requestInheritsFrom(request))

/**
 * Whether a record's edge to one parent stays the plain relation rather than its `_confidential` twin. An open record
 * keeps every plain edge and a marked one twins them all. A record confidential only through its parents keeps the
 * plain edge to each confidential parent, so whoever can see that parent still sees the child: that parent's own
 * broad edges are already twinned, so the plain hop reaches only its owners, contacts, grantees and cleared readers.
 * It twins the edge to each open parent, whose broad readers would otherwise reach the child.
 */
export const keepsPlainParentEdge = (
  record: { isMarked: boolean; isConfidential: boolean },
  isParentConfidential: boolean
): boolean => !record.isConfidential || (!record.isMarked && isParentConfidential)
